<template>
  <div class="ac">
    <input
      :id="id"
      ref="input"
      class="app-field-control"
      type="email"
      inputmode="email"
      autocomplete="off"
      autocapitalize="off"
      spellcheck="false"
      role="combobox"
      aria-autocomplete="list"
      :value="value"
      :placeholder="placeholder"
      :aria-expanded="abierto ? 'true' : 'false'"
      :aria-controls="listaId"
      :aria-activedescendant="abierto ? opcionId(activa) : null"
      :aria-invalid="invalid ? 'true' : null"
      :aria-describedby="correccion ? listaId + '-correccion' : null"
      :data-testid="testid"
      @focus="enfocado = true"
      @input="alEscribir"
      @keydown="alTeclear"
      @blur="alSalir"
    />

    <ul
      v-if="abierto"
      :id="listaId"
      role="listbox"
      class="ac-lista"
      :class="{ arriba }"
      :style="{ maxHeight: altoMaximo + 'px' }"
      :data-testid="testid + '-sugerencias'"
    >
      <li
        v-for="(sugerencia, i) in sugerencias"
        :id="opcionId(i)"
        :key="sugerencia"
        role="option"
        class="ac-opcion"
        :class="{ activa: i === activa }"
        :aria-selected="i === activa ? 'true' : 'false'"
        @mousedown.prevent="elegir(sugerencia)"
        @mousemove="activa = i"
      >
        <span class="ac-usuario">{{ partes(sugerencia).usuario }}</span><span class="ac-dominio">@{{ partes(sugerencia).dominio }}</span>
      </li>
    </ul>

    <p v-if="correccion" :id="listaId + '-correccion'" class="ac-correccion" :data-testid="testid + '-correccion'">
      ¿Quisiste decir
      <button type="button" @mousedown.prevent @click="elegir(correccion)">{{ correccion }}</button>?
    </p>
  </div>
</template>

<script>
/*
 * AppCorreo — campo de correo con autocompletado de dominio y corrección.
 *
 * - Al escribir "ana@" sugiere dominios en orden de uso (hotmail.com, gmail.com…).
 * - Al salir limpia espacios, mayúsculas y punto final, y si el dominio se
 *   parece a uno conocido ofrece "¿Quisiste decir…?" (no corrige solo: un
 *   dominio de empresa puede parecerse a uno conocido).
 * - Flechas y Enter para elegir; Esc oculta las sugerencias.
 */
import { corregirCorreo, normalizarCorreo, sugerenciasDeCorreo } from '@/utils/correo'
import { posicionLista } from '@/utils/posicion'

const MAX_SUGERENCIAS = 5
const ALTO_SUGERENCIA = 36
let consecutivo = 0

export default {
  name: 'AppCorreo',
  props: {
    value: { type: String, default: '' },
    id: { type: String, default: null },
    placeholder: { type: String, default: 'cliente@correo.mx' },
    invalid: { type: Boolean, default: false },
    testid: { type: String, default: 'correo' },
  },
  data() {
    return {
      enfocado: false,
      descartado: false,
      activa: 0,
      arriba: false,
      altoMaximo: MAX_SUGERENCIAS * ALTO_SUGERENCIA + 12,
      correccion: null,
      listaId: `ac-lista-${++consecutivo}`,
    }
  },
  computed: {
    sugerencias() {
      return sugerenciasDeCorreo(this.value, MAX_SUGERENCIAS)
    },
    abierto() {
      return this.enfocado && !this.descartado && this.sugerencias.length > 0
    },
  },
  watch: {
    // Dentro de un modal, si abajo no cabe la lista se abre hacia arriba
    abierto(valor) {
      if (!valor) return
      const posicion = posicionLista(this.$el, this.sugerencias.length * ALTO_SUGERENCIA + 12)
      this.arriba = posicion.arriba
      this.altoMaximo = posicion.altoMaximo
    },
    value(nuevo) {
      // La corrección solo aplica al valor con el que se calculó
      if (this.correccion && normalizarCorreo(nuevo) !== this.valorCorregido) this.correccion = null
    },
  },
  methods: {
    opcionId(i) {
      return `${this.listaId}-${i}`
    },
    partes(correo) {
      const arroba = correo.lastIndexOf('@')
      return { usuario: correo.slice(0, arroba), dominio: correo.slice(arroba + 1) }
    },
    alEscribir(event) {
      // Un correo no lleva espacios: se quitan al teclear
      const limpio = event.target.value.replace(/\s+/g, '')
      if (limpio !== event.target.value) event.target.value = limpio
      this.descartado = false
      this.activa = 0
      this.correccion = null
      this.$emit('input', limpio)
    },
    alTeclear(event) {
      if (!this.abierto) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.activa = Math.min(this.activa + 1, this.sugerencias.length - 1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.activa = Math.max(this.activa - 1, 0)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.elegir(this.sugerencias[this.activa])
      } else if (event.key === 'Escape') {
        // Esc oculta las sugerencias, no cierra el modal
        event.stopPropagation()
        this.descartado = true
      }
    },
    elegir(correo) {
      this.$emit('input', correo)
      this.correccion = null
      this.descartado = true
    },
    alSalir() {
      this.enfocado = false
      const normalizado = normalizarCorreo(this.value)
      if (normalizado !== this.value) this.$emit('input', normalizado)
      this.correccion = corregirCorreo(normalizado)
      this.valorCorregido = normalizado
      this.$emit('blur')
    },
  },
}
</script>

<style scoped>
.ac {
  position: relative;
}
.ac-lista {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: 0;
  z-index: 50;
  margin: 0;
  padding: 6px;
  border: 1px solid #e4e9ef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(16, 42, 67, 0.16);
  overflow-y: auto;
  list-style: none;
}
.ac-lista.arriba {
  top: auto;
  bottom: calc(100% + 6px);
}
.ac-opcion {
  overflow: hidden;
  padding: 8px 10px;
  border-radius: 8px;
  color: #607187;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.ac-opcion.activa {
  background: #edf3f8;
}
.ac-dominio {
  color: #0b1b2e;
  font-weight: 600;
}
.ac-correccion {
  margin: 4px 0 0;
  color: #92400e;
  font-size: 12px;
}
.ac-correccion button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #0a4f9c;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
.ac-correccion button:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
</style>
